// frontend/src/features/posts/PostForm.js
/**
 * PostForm
 * -------
 * Creates/edits a post with:
 *  - "Moments" (sections) using TinyMCE for editable sections
 *  - Read-only sections rendered as HTML
 *  - Post Inbox media (section = null)
 *  - Section media (media attached to a moment)
 *  - Drag/drop move + reorder (with backend authority on permissions)
 *  - Per-section collaborator grant UI (Phase 2C groundwork)
 *  - Location + timezone inferred from map pin (Google APIs via mapUtils)
 *  - Optional historical mode (date/time for new posts)
 *
 * Notes:
 *  - Backend is the final authority on permissions. UI is "helpful" only.
 *  - We keep post.content in sync (back-compat) with section #1 when allowed.
 *
 * Requirements:
 *  - TinyMCE React package:
 *      npm i @tinymce/tinymce-react tinymce
 */

import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useDropzone } from "react-dropzone";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import styled from "styled-components";
import { Editor } from "@tinymce/tinymce-react";

import { useSpinner } from "../../context/SpinnerContext";
import { useAuth } from "../../context/AuthContext";
import { useBlog } from "../../context/BlogContext";
import { useThemeContext } from "../../context/ThemeContext";

import axios from "../../services/axios";
import { fetchLocationName, fetchTimeZone } from "../map/mapUtils";
import { momentLabelFromAuthor, bestAuthorName } from "./postUtils";

const MapPicker = lazy(() => import("../map/MapPicker"));

/* =============================================================================
 * Constants
 * ========================================================================== */

const DEFAULT_COORDS = [49.2827, -123.1207]; // Vancouver fallback
const GEOLOCATION_TIMEOUT_MS = 5000;
const MAX_GRANT_ROWS = 50; // safety: prevent huge UI rendering if list explodes

// Media menu positioning
const MENU_MIN_WIDTH = 180;
const MENU_VIEWPORT_PAD = 8;
const MENU_GAP_Y = 6;

/* =============================================================================
 * Styled Components
 * ========================================================================== */

const Form = styled.form`
  margin-bottom: 2rem;
`;

const Input = styled.input`
  padding: 10px;
  width: 100%;
  margin-bottom: 10px;
  border: 1px solid ${({ theme }) => theme.inputBorder || "#ccc"};
  border-radius: 6px;
`;

const Select = styled.select`
  padding: 10px;
  width: 100%;
  margin-bottom: 10px;
  border: 1px solid ${({ theme }) => theme.inputBorder || "#ccc"};
  border-radius: 6px;
`;

const Label = styled.label`
  display: block;
  margin: 10px 0 4px;
`;

const Dropzone = styled.div`
  border: 2px dashed ${({ theme }) => theme.primary || "#2cc2d4"};
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  background-color: ${({ theme }) => theme.dropzoneBg || "#fafafa"};
  margin-top: 1rem;
  border-radius: 6px;
`;

const MapWrapper = styled.div`
  margin: 1rem 0;
  position: relative;
  z-index: 1;
`;

const MediaPreview = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const DndMediaList = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Preview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid ${({ theme }) => theme.inputBorder || "#ccc"};
  border-radius: 5px;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 5px;
    cursor: pointer;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  background-color: red;
  color: white;
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 1rem;

  button {
    background-color: ${({ theme }) => theme.primary || "#007bff"};
    color: white;
    padding: 0.6rem 1.2rem;
    border: none;
    border-radius: 6px;
    font-weight: bold;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
      background-color: ${({ theme }) => theme.secondary || "#0056b3"};
    }
  }
`;

const MutedMessage = styled.p`
  color: gray;
  font-style: italic;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 6px;

  input {
    transform: translateY(1px);
  }

  span {
    font-weight: 600;
  }
`;

const DragHandle = styled.div`
  position: absolute;
  left: 4px;
  top: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-weight: 900;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  cursor: grab;
  user-select: none;
`;

/** Sections (Moments) UI wrappers */
const SectionCard = styled.div`
  border: 1px solid ${({ theme }) => theme.inputBorder || "#ddd"};
  border-radius: 10px;
  padding: 12px;
  margin: 14px 0;
  background: ${({ theme }) => theme.cardBg || "#fff"};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;

  .meta {
    display: flex;
    gap: 10px;
    align-items: center;
    font-weight: 700;
  }

  .sub {
    font-weight: 400;
    opacity: 0.75;
  }
`;

const SmallBtn = styled.button`
  background: ${({ theme }) => theme.primary || "#007bff"};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 10px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover {
    background: ${({ theme }) => theme.secondary || "#0056b3"};
  }
`;

const DangerBtn = styled(SmallBtn)`
  background: #d33;
  &:hover {
    background: #b22;
  }
`;

const DroppableShell = styled.div`
  padding: 10px;
  border-radius: 10px;
  min-height: 112px; /* visible even when empty */
  border: 2px dashed
    ${({ $state }) =>
      $state === "allowed"
        ? "rgba(0,180,0,0.65)"
        : $state === "blocked"
          ? "rgba(200,0,0,0.65)"
          : "rgba(0,0,0,0.12)"};
  background: ${({ $state }) =>
    $state === "allowed"
      ? "rgba(0,180,0,0.06)"
      : $state === "blocked"
        ? "rgba(200,0,0,0.06)"
        : "rgba(0,0,0,0.02)"};
  transition: background 120ms ease, border-color 120ms ease;
`;

const InlineNotice = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(200, 0, 0, 0.25);
  background: rgba(200, 0, 0, 0.06);
  color: rgba(120, 0, 0, 0.9);
  font-weight: 600;
`;

const MetricsPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.inputBorder || "#ddd"};
  border-radius: 10px;
  padding: 12px;
  margin: 14px 0;
  background: ${({ theme }) => theme.cardBg || "#fff"};
`;

/** Per-media 3-dot menu */
const MenuBtn = styled.button`
  position: absolute;
  right: 28px;
  top: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-weight: 900;
  cursor: pointer;
`;

/**
 * Menu is rendered in a portal (document.body) and positioned fixed.
 * This prevents clipping and lets us clamp inside the viewport.
 */
const Menu = styled.div`
  position: fixed;
  min-width: ${MENU_MIN_WIDTH}px;
  max-width: min(320px, calc(100vw - ${MENU_VIEWPORT_PAD * 2}px));
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.15);
  z-index: 99999;
  overflow: hidden;
`;

const MenuItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  background: #fff;
  cursor: pointer;
  font-weight: 650;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Media strip label + hint */
const MediaStripLabel = styled.div`
  margin-top: 10px;
  margin-bottom: 6px;
  font-weight: 800;
  opacity: 0.9;
`;

const EmptyDroppableHint = styled.div`
  padding: 12px;
  border-radius: 10px;
  color: rgba(0, 0, 0, 0.55);
  font-weight: 650;
  text-align: center;
`;

const ReadOnlyHtml = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-radius: 10px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.02);
  color: inherit;
  line-height: 1.45;

  p { margin: 0 0 0.8em; }
  p:last-child { margin-bottom: 0; }
  img { max-width: 100%; height: auto; }
  blockquote { margin: 0.8em 0; padding-left: 12px; opacity: 0.9; border-left: 3px solid rgba(0,0,0,0.2); }
`;

/* =============================================================================
 * Helpers (date/time + comparisons)
 * ========================================================================== */

function formatDateForInputInTZ(dateString, timeZone) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";

  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = dtf.formatToParts(d).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function nowForInputInTZ(timeZone) {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatDateForInputInTZ(new Date().toISOString(), tz);
}

const approxEqual = (a, b, eps = 1e-8) => Math.abs(a - b) < eps;

function normalizeIncomingSections(post, defaultAuthorName) {
  if (Array.isArray(post?.sections) && post.sections.length > 0) {
    return post.sections
      .map((s) => ({
        id: s.id,
        order: Number(s.order) || 1,
        title: s.title || "",
        content: s.content || "",
        author: s.author || s.author_name || s.author_username || null,
        author_username: s.author_username || null,
        author_id: s.author_id || null,
        can_edit: !!s.can_edit,
        can_manage_media: !!s.can_manage_media,
        media: Array.isArray(s.media) ? s.media : [],
        pending_media: [],
      }))
      .sort((a, b) => (a.order - b.order) || ((a.id || 0) - (b.id || 0)));
  }

  return [
    {
      id: null,
      order: 1,
      title: "",
      content: post?.content || "",
      author: post?.owner || defaultAuthorName || null,
      author_username: post?.owner || null,
      author_id: null,
      can_edit: true,
      can_manage_media: true,
      media: [],
      pending_media: [],
    },
  ];
}

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

/* =============================================================================
 * TinyMCE Editor Wrapper
 * ========================================================================== */

function TinyMceEditor({ value, onChange, disabled, editorRef }) {
  return (
    <Editor
      apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
      onInit={(_, editor) => {
        if (editorRef) editorRef.current = editor;
      }}
      value={value ?? ""}
      onEditorChange={(html) => onChange?.(html)}
      disabled={!!disabled}
      init={{
        height: 320,
        menubar: false,
        branding: false,
        statusbar: true,
        plugins: "link lists code autoresize",
        toolbar:
          "undo redo | blocks | bold italic underline | bullist numlist | link | code",
        content_style:
          "body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; font-size: 16px; }",
      }}
    />
  );
}

function SectionMediaDropzone({ theme, onFiles }) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles?.length) return;
      onFiles(acceptedFiles);
    },
    [onFiles]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: false,
    noKeyboard: true,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "video/*": [".mp4", ".mov", ".avi"],
    },
  });

  return (
    <div {...getRootProps()}>
      <Dropzone theme={theme}>
        <input {...getInputProps()} />
        <p>Drag and drop images/videos here, or click to add to this moment</p>
      </Dropzone>
    </div>
  );
}

/* =============================================================================
 * PostForm
 * ========================================================================== */

const PostForm = ({ post = {}, onSave, onCancel, isEditing, timezones = [] }) => {
  const { blog: selected_blog } = useBlog();
  const { theme } = useThemeContext();
  const { setLoading } = useSpinner();
  const { can, user } = useAuth();

  const canCreate = can("posts:create");
  const canUpdateAny = can("posts:update_any");
  const canUpdateOwn = can("posts:update_own") && post?.owner === user?.username;

  const isOwnerOrAdmin = useMemo(() => {
    return post?.owner === user?.username || canUpdateAny;
  }, [post?.owner, user?.username, canUpdateAny]);

  // Structural edit access (post-level)
  const canEnterEditMode = useMemo(() => {
    if (!isEditing) return canCreate;
    return !!(canUpdateAny || canUpdateOwn || post?.can_edit_post);
  }, [isEditing, canCreate, canUpdateAny, canUpdateOwn, post?.can_edit_post]);

  const canAddMoment = useMemo(() => {
    if (!isEditing) return canCreate;
    return !!(canUpdateAny || canUpdateOwn || post?.can_add_sections);
  }, [isEditing, canCreate, canUpdateAny, canUpdateOwn, post?.can_add_sections]);

  const canReorderMoments = useMemo(() => {
    if (!isEditing) return false;
    return !!(canUpdateAny || canUpdateOwn || post?.can_edit_post);
  }, [isEditing, canUpdateAny, canUpdateOwn, post?.can_edit_post]);

  // Timeline controls (meta policy)
  const canEditTimeline = canUpdateAny || canUpdateOwn;
  const showDateControls =
    canEditTimeline &&
    (isEditing || localStorage.getItem("historical_mode") === "true");

  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [timezone, setTimezone] = useState("");
  const [coordinates, setCoordinates] = useState(DEFAULT_COORDS);

  const [historicalMode, setHistoricalMode] = useState(
    () => localStorage.getItem("historical_mode") === "true"
  );

  useEffect(() => {
    localStorage.setItem("historical_mode", historicalMode ? "true" : "false");
  }, [historicalMode]);

  const [date, setDate] = useState(() => {
    const tz = post.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatDateForInputInTZ(post.post_date, tz);
  });
  const dateDirtyRef = useRef(false);

  const currentAuthorName =
    user?.first_name?.trim() || user?.username?.trim() || "Author";

  const [sections, setSections] = useState(() =>
    normalizeIncomingSections(post, currentAuthorName)
  );

  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const [dragNotice, setDragNotice] = useState(null);
  const [activeDragMediaId, setActiveDragMediaId] = useState(null);

  // Menu state + positioning
  const [openMenuKey, setOpenMenuKey] = useState(null);
  const menuBtnRefs = useRef(new Map()); // key -> HTMLElement
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  const [collabMembers, setCollabMembers] = useState([]);
  const [sectionGrantUIOpen, setSectionGrantUIOpen] = useState(null);
  const [sectionGrantDrafts, setSectionGrantDrafts] = useState({});

  const [postMetrics, setPostMetrics] = useState(null);
  const [postMetricsTs, setPostMetricsTs] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Scroll + focus when adding a new moment
  const sectionElRefs = useRef({}); // key -> DOM element
  const editorRefs = useRef({}); // key -> { current: tinymce editor }
  const [lastAddedKey, setLastAddedKey] = useState(null);

  // Cleanup staged object URLs
  useEffect(() => {
    return () => {
      (newImages || []).forEach((f) => f?.preview && URL.revokeObjectURL(f.preview));
      (sections || []).forEach((s) =>
        (s?.pending_media || []).forEach((f) => f?.preview && URL.revokeObjectURL(f.preview))
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTitle(post.title || "");
    setLocation(post.location || "");
    setTimezone(post.timezone || "");

    setSections(normalizeIncomingSections(post, currentAuthorName));

    dateDirtyRef.current = false;
    const tzForDisplay = post.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (post.post_date) {
      setDate(formatDateForInputInTZ(post.post_date, tzForDisplay));
    } else {
      setDate(historicalMode ? nowForInputInTZ(tzForDisplay) : "");
    }

    setNewImages([]);
    const normalized = (post.post_media || []).map((m) => ({
      ...m,
      url: m.media_url || m.url,
    }));
    setExistingImages(normalized);
    setImagesToDelete([]);

    const isNew = !post.id;

    if (!isNew && post.latitude != null && post.longitude != null) {
      const lat = Number(post.latitude);
      const lng = Number(post.longitude);
      setCoordinates([
        Number.isNaN(lat) ? DEFAULT_COORDS[0] : lat,
        Number.isNaN(lng) ? DEFAULT_COORDS[1] : lng,
      ]);
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoordinates([pos.coords.latitude, pos.coords.longitude]),
        () => setCoordinates(DEFAULT_COORDS),
        { timeout: GEOLOCATION_TIMEOUT_MS }
      );
    } else {
      setCoordinates(DEFAULT_COORDS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, currentAuthorName]);

  useEffect(() => {
    if (!timezone) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) setTimezone(tz);
    }
  }, [timezone]);

  useEffect(() => {
    if (!showDateControls) return;
    if (dateDirtyRef.current) return;

    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (post?.post_date) {
      setDate(formatDateForInputInTZ(post.post_date, tz));
    } else if (historicalMode) {
      setDate((prev) => prev || nowForInputInTZ(tz));
    }
  }, [timezone, showDateControls, post?.post_date, historicalMode]);

  useEffect(() => {
    if (!post?.id) return;
    let cancelled = false;

    (async () => {
      setMetricsLoading(true);
      try {
        const [summaryRes, tsRes] = await Promise.all([
          axios.get(`/api/metrics/posts/${post.id}/summary/`),
          axios.get(`/api/metrics/posts/${post.id}/timeseries/?days=30`),
        ]);
        if (cancelled) return;
        setPostMetrics(summaryRes.data);
        setPostMetricsTs(tsRes.data);
      } catch (err) {
        if (!cancelled) {
          setPostMetrics(null);
          setPostMetricsTs(null);
        }
      } finally {
        if (!cancelled) setMetricsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [post?.id]);

  useEffect(() => {
    if (isEditing) return;
    if (!showDateControls) return;

    if (historicalMode && !date) {
      const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      dateDirtyRef.current = false;
      setDate(nowForInputInTZ(tz));
    }

    if (!historicalMode && !isEditing) {
      dateDirtyRef.current = false;
      setDate("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalMode]);

  const lastLookupRef = useRef({ lat: null, lng: null });

  useEffect(() => {
    const [lat, lng] = coordinates.map(Number);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const last = lastLookupRef.current;
    if (
      last.lat != null &&
      last.lng != null &&
      approxEqual(last.lat, lat) &&
      approxEqual(last.lng, lng)
    ) {
      return;
    }
    lastLookupRef.current = { lat, lng };

    let cancelled = false;

    (async () => {
      try {
        const [loc, tzFromPin] = await Promise.all([
          fetchLocationName(lat, lng),
          fetchTimeZone(lat, lng),
        ]);

        if (cancelled) return;

        if (loc && loc !== location) setLocation(loc);
        if (tzFromPin && tzFromPin !== timezone) setTimezone(tzFromPin);
      } catch {
        // never block editing
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates]);

  // Scroll into view + focus TinyMCE when a new moment is added
  useEffect(() => {
    if (!lastAddedKey) return;

    const el = sectionElRefs.current[lastAddedKey];
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });

    // TinyMCE mounts async; give it a tick then focus if available
    setTimeout(() => {
      const editor = editorRefs.current[lastAddedKey]?.current;
      if (editor && typeof editor.focus === "function") {
        editor.focus();
      }
    }, 80);

    setLastAddedKey(null);
  }, [lastAddedKey]);

  /* =============================================================================
   * API helpers
   * ========================================================================== */

  const uploadToPostInbox = useCallback(async (postId, files) => {
    const form = new FormData();
    files.forEach((f) => form.append("media", f));
    const config = { headers: { "Content-Type": "multipart/form-data" } };
    const res = await axios.post(`/api/posts/${postId}/media/`, form, config);
    return Array.isArray(res.data) ? res.data : [];
  }, []);

  const removeSectionMedia = useCallback(
    async (sectionIdx, mediaId) => {
      const section = sections[sectionIdx];
      const target = (section?.media || []).find((m) => m.id === mediaId);

      // If the backend says you can delete it, do it immediately
      if (post?.id && target?.can_delete) {
        try {
          await axios.delete(`/api/media/${mediaId}/`);
        } catch (e) {
          console.error("Delete section media failed:", e);
          setDragNotice("Delete failed.");
          return;
        }
      } else {
        // Otherwise, just remove locally
      }

      // Remove from UI
      setSections((prev) =>
        prev.map((s, i) =>
          i === sectionIdx ? { ...s, media: (s.media || []).filter((m) => m.id !== mediaId) } : s
        )
      );
    },
    [sections, post?.id]
  );

  const uploadToSection = useCallback(async (sectionId, files) => {
    const form = new FormData();
    files.forEach((f) => form.append("media", f));
    const config = { headers: { "Content-Type": "multipart/form-data" } };
    const res = await axios.post(`/api/sections/${sectionId}/media/`, form, config);
    return Array.isArray(res.data) ? res.data : [];
  }, []);

  const refreshFromServer = useCallback(async () => {
    if (!post?.id) return;
    try {
      const res = await axios.get(`/api/posts/${post.id}/`);
      const p = res.data;

      setSections(normalizeIncomingSections(p, currentAuthorName));
      const normalized = (p.post_media || []).map((m) => ({
        ...m,
        url: m.media_url || m.url,
      }));
      setExistingImages(normalized);
    } catch (e) {
      console.error("Failed to refresh post:", e);
    }
  }, [post?.id, currentAuthorName]);

  const moveMedia = useCallback(async (mediaId, targetSectionIdOrNull, afterMediaIdOrNull) => {
    const payload = { section: targetSectionIdOrNull, after: afterMediaIdOrNull };
    await axios.post(`/api/media/${mediaId}/move/`, payload);
  }, []);

  const reorderMedia = useCallback(async (targetSectionIdOrNull, orderedIds) => {
    const payload = { section: targetSectionIdOrNull, order: orderedIds };
    await axios.post("/api/media/reorder/", payload);
  }, []);

  /* ---------------------------------------------------------------------------
   * Collaborators + per-section grant loading
   * ------------------------------------------------------------------------ */

  const loadCollaborators = useCallback(async () => {
    if (!post?.id) return;

    // Only owner/admin can fetch collaborator roster
    if (!isOwnerOrAdmin) {
      setCollabMembers([]);
      return;
    }

    try {
      const res = await axios.get(`/api/posts/${post.id}/collaborators/`);
      setCollabMembers(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCollabMembers([]);
    }
  }, [post?.id, isOwnerOrAdmin]);

  const loadSectionGrants = useCallback(async (sectionId) => {
    if (!sectionId) return;
    try {
      const res = await axios.get(`/api/sections/${sectionId}/grants/`);
      const grants = Array.isArray(res.data) ? res.data : [];

      setSectionGrantDrafts((prev) => {
        const next = { ...(prev || {}) };
        next[sectionId] = next[sectionId] || {};

        grants.forEach((g) => {
          next[sectionId][g.grantee_username] = {
            can_edit_content: !!g.can_edit_content,
            can_manage_media: !!g.can_manage_media,
          };
        });

        return next;
      });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadCollaborators();
  }, [loadCollaborators]);

  const saveSectionGrants = async (sectionId) => {
    if (!sectionId) return;

    try {
      const draft = sectionGrantDrafts?.[sectionId] || {};
      const grants = Object.entries(draft).map(([username, perms]) => ({
        username,
        can_edit_content: !!perms.can_edit_content,
        can_manage_media: !!perms.can_manage_media,
      }));

      await axios.put(`/api/sections/${sectionId}/grants/`, { grants });

      await loadSectionGrants(sectionId);

      setSectionGrantUIOpen(null);
      setDragNotice(null);
    } catch (e) {
      console.error("Failed to save collaborators:", e);
      setDragNotice("Failed to save collaborators.");
    }
  };

  /* =============================================================================
   * Media upload handlers (Inbox + Section)
   * ========================================================================== */

  const canUploadInbox = useMemo(() => {
    return !post?.id || post?.owner === user?.username || canUpdateAny;
  }, [post?.id, post?.owner, user?.username, canUpdateAny]);

  const onDropInbox = useCallback(
    async (acceptedFiles) => {
      const withPreview = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          _tmpId: `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        })
      );

      if (post?.id) {
        const placeholders = withPreview.map((file) => ({
          id: file._tmpId,
          preview_url: file.preview,
          media_url: file.preview,
          thumbnail_url: file.preview,
          content_type: file.type || "application/octet-stream",
          processed: !String(file.type || "").startsWith("video/"),
          is_pending: true,
          can_delete: true,
          can_move: false,
        }));
        setExistingImages((prev) => [...prev, ...placeholders]);

        try {
          const created = await uploadToPostInbox(post.id, acceptedFiles);
          const normalized = created.map((m) => ({ ...m, url: m.media_url || m.url }));
          setExistingImages((prev) => [
            ...prev.filter((img) => !placeholders.find((p) => p.id === img.id)),
            ...normalized,
          ]);
          // cleanup previews for staged
          withPreview.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
          return;
        } catch (e) {
          console.error("Inbox upload failed:", e);
          setExistingImages((prev) =>
            prev.filter((img) => !placeholders.find((p) => p.id === img.id))
          );
          withPreview.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
          setDragNotice("Upload failed.");
        }
      }

      setNewImages((prev) => [...prev, ...withPreview]);
    },
    [post?.id, uploadToPostInbox]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDropInbox,
    disabled: !canUploadInbox,
    noClick: false,
    noKeyboard: true,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "video/*": [".mp4", ".mov", ".avi"],
    },
  });


  const removeExistingImage = useCallback(
    async (id) => {
      const target = existingImages.find((i) => i.id === id);

      if (post?.id && target?.can_delete) {
        try {
          await axios.delete(`/api/media/${id}/`);
        } catch (e) {
          console.error("Delete media failed:", e);
          setDragNotice("Delete failed.");
          return;
        }
      } else {
        setImagesToDelete((prev) => [...prev, id]);
      }

      setExistingImages((prev) => prev.filter((img) => img.id !== id));
    },
    [existingImages, post?.id]
  );

  const removeNewImage = useCallback((index) => {
    setNewImages((prev) => {
      const target = prev[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addMediaToSection = useCallback(
    async (idx, fileArr) => {
      if (!Array.isArray(fileArr) || fileArr.length === 0) return;

      const section = sections[idx];

      if (section?.id) {
        const staged = fileArr.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
            _tmpId: `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          })
        );
        setSections((prev) =>
          prev.map((s, i) =>
            i === idx ? { ...s, pending_media: [...(s.pending_media || []), ...staged] } : s
          )
        );
        try {
          const created = await uploadToSection(section.id, fileArr);
          setSections((prev) =>
            prev.map((s, i) => {
              if (i !== idx) return s;
              const pending = (s.pending_media || []).filter(
                (p) => !staged.find((st) => st._tmpId === p._tmpId)
              );
              return { ...s, media: [...(s.media || []), ...created], pending_media: pending };
            })
          );
          staged.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
          return;
        } catch (e) {
          console.error("Section upload failed:", e);
          setSections((prev) =>
            prev.map((s, i) => {
              if (i !== idx) return s;
              const pending = (s.pending_media || []).filter(
                (p) => !staged.find((st) => st._tmpId === p._tmpId)
              );
              return { ...s, pending_media: pending };
            })
          );
          staged.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
          setDragNotice("Section upload failed.");
        }
      }

      const staged = fileArr.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      );
      setSections((prev) =>
        prev.map((s, i) =>
          i === idx ? { ...s, pending_media: [...(s.pending_media || []), ...staged] } : s
        )
      );
    },
    [sections, uploadToSection]
  );

  const removeSection = useCallback(
    async (idx) => {
      const target = sections[idx];
      if (!target) return;

      // Never delete section order=1
      if (Number(target.order) === 1) {
        setDragNotice("Section 1 cannot be deleted.");
        return;
      }

      // New/unsaved section: local only
      if (!target.id) {
        (target.pending_media || []).forEach((f) => f?.preview && URL.revokeObjectURL(f.preview));
        setSections((prev) => prev.filter((_, i) => i !== idx));
        return;
      }

      // Persisted section: delete via API immediately
      try {
        setDragNotice(null);
        await axios.delete(`/api/sections/${target.id}/`);

        (target.pending_media || []).forEach((f) => f?.preview && URL.revokeObjectURL(f.preview));
        setSections((prev) => prev.filter((_, i) => i !== idx));

        await refreshFromServer();
      } catch (e) {
        console.error("Failed to delete section:", e);
        setDragNotice("Delete failed (permission?). Refreshing.");
        await refreshFromServer();
      }
    },
    [sections, refreshFromServer]
  );

  /* =============================================================================
   * Drag/drop helpers
   * ========================================================================== */

  const findSectionIndexById = useCallback(
    (sectionId) => {
      const sid = Number(sectionId);
      return sections.findIndex((s) => Number(s.id) === sid);
    },
    [sections]
  );

  const getMediaListForDroppable = useCallback(
    (droppableId) => {
      if (droppableId === "inbox") return existingImages;
      if (droppableId.startsWith("section-")) {
        const sid = droppableId.replace("section-", "");
        const idx = findSectionIndexById(sid);
        if (idx >= 0) return sections[idx].media || [];
      }
      return [];
    },
    [existingImages, sections, findSectionIndexById]
  );

  const setMediaListForDroppable = useCallback(
    (droppableId, nextList) => {
      if (droppableId === "inbox") {
        setExistingImages(nextList);
        return;
      }
      if (droppableId.startsWith("section-")) {
        const sid = droppableId.replace("section-", "");
        const idx = findSectionIndexById(sid);
        if (idx >= 0) {
          setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, media: nextList } : s)));
        }
      }
    },
    [findSectionIndexById]
  );

  const canDropInto = useCallback(
    (droppableId, media) => {
      if (!media?.can_move) return false;

      if (droppableId === "inbox") {
        return isOwnerOrAdmin || (media?.uploaded_by && media.uploaded_by === user?.username);
      }

      if (droppableId.startsWith("section-")) {
        const sid = droppableId.replace("section-", "");
        const idx = findSectionIndexById(sid);
        if (idx < 0) return false;
        const section = sections[idx];
        return isOwnerOrAdmin || !!section?.can_edit || !!section?.can_manage_media;
      }

      return false;
    },
    [isOwnerOrAdmin, user?.username, findSectionIndexById, sections]
  );

  const getDraggedMedia = useCallback(() => {
    if (!activeDragMediaId) return null;

    const inboxHit = existingImages.find((m) => m.id === activeDragMediaId);
    if (inboxHit) return inboxHit;

    for (const sec of sections) {
      const hit = (sec.media || []).find((m) => m.id === activeDragMediaId);
      if (hit) return hit;
    }
    return null;
  }, [activeDragMediaId, existingImages, sections]);

  /* =============================================================================
   * Media menu (portal + clamped position)
   * ========================================================================== */

  const getMoveTargets = useCallback(
    (media) => {
      if (!media?.can_move) return [];
      const targets = [{ label: "Post Inbox", sectionId: null, droppableId: "inbox" }];

      for (const sec of sections) {
        if (!sec?.id) continue;
        const dId = `section-${sec.id}`;

        if (canDropInto(dId, media)) {
          const authorName = bestAuthorName(sec) || sec.author || currentAuthorName;
          targets.push({
            label: momentLabelFromAuthor(authorName),
            sectionId: Number(sec.id),
            droppableId: dId,
          });
        }
      }
      return targets;
    },
    [sections, canDropInto, currentAuthorName]
  );

  const menuMove = useCallback(
    async (media, targetSectionIdOrNull) => {
      if (!post?.id) return;

      try {
        setDragNotice(null);
        await moveMedia(media.id, targetSectionIdOrNull, null);
        await refreshFromServer();
      } catch (e) {
        console.error("Menu move failed:", e);
        setDragNotice("Move failed (permission or conflict). Refreshing.");
        await refreshFromServer();
      } finally {
        setOpenMenuKey(null);
      }
    },
    [post?.id, moveMedia, refreshFromServer]
  );

  // Initial compute when menu opens (based on button rect)
  useLayoutEffect(() => {
    if (!openMenuKey) {
      setMenuPos(null);
      return;
    }

    const btn = menuBtnRefs.current.get(openMenuKey);
    if (!btn) return;

    const r = btn.getBoundingClientRect();

    const desiredWidth = Math.min(
      320,
      Math.max(MENU_MIN_WIDTH, Math.floor(window.innerWidth * 0.6))
    );
    const left = clamp(
      r.right - desiredWidth,
      MENU_VIEWPORT_PAD,
      window.innerWidth - desiredWidth - MENU_VIEWPORT_PAD
    );

    const top = clamp(
      r.bottom + MENU_GAP_Y,
      MENU_VIEWPORT_PAD,
      window.innerHeight - MENU_VIEWPORT_PAD
    );

    setMenuPos({ left, top, desiredWidth });
  }, [openMenuKey]);

  // Refine after menu renders
  useLayoutEffect(() => {
    if (!openMenuKey) return;
    if (!menuPos) return;
    const btn = menuBtnRefs.current.get(openMenuKey);
    const menuEl = menuRef.current;
    if (!btn || !menuEl) return;

    const br = btn.getBoundingClientRect();
    const mr = menuEl.getBoundingClientRect();

    const clampedLeft = clamp(
      menuPos.left,
      MENU_VIEWPORT_PAD,
      window.innerWidth - mr.width - MENU_VIEWPORT_PAD
    );

    let top = br.bottom + MENU_GAP_Y;
    if (top + mr.height > window.innerHeight - MENU_VIEWPORT_PAD) {
      const above = br.top - MENU_GAP_Y - mr.height;
      if (above >= MENU_VIEWPORT_PAD) top = above;
      else top = clamp(top, MENU_VIEWPORT_PAD, window.innerHeight - mr.height - MENU_VIEWPORT_PAD);
    }

    if (clampedLeft !== menuPos.left || top !== menuPos.top) {
      setMenuPos((prev) => ({ ...(prev || {}), left: clampedLeft, top }));
    }
  }, [openMenuKey, menuPos]);

  useEffect(() => {
    if (!openMenuKey) return;

    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenuKey(null);
    };

    const onDown = (e) => {
      const menuEl = menuRef.current;
      const btn = menuBtnRefs.current.get(openMenuKey);

      if (menuEl && menuEl.contains(e.target)) return;
      if (btn && btn.contains(e.target)) return;

      setOpenMenuKey(null);
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [openMenuKey]);

  const renderMediaMenu = useCallback(
    (media, key, onDelete) => {
      if (!(media?.can_move || media?.can_delete)) return null;

      const isOpen = openMenuKey === key;
      const targets = getMoveTargets(media);

      return (
        <>
          <MenuBtn
            type="button"
            ref={(el) => {
              if (el) menuBtnRefs.current.set(key, el);
              else menuBtnRefs.current.delete(key);
            }}
            onClick={() => setOpenMenuKey((prev) => (prev === key ? null : key))}
            title="Actions"
          >
            ⋯
          </MenuBtn>

          {isOpen && menuPos
            ? createPortal(
                <Menu
                  ref={menuRef}
                  style={{
                    left: menuPos.left,
                    top: menuPos.top,
                    minWidth: MENU_MIN_WIDTH,
                  }}
                >
                  {targets.map((t) => (
                    <MenuItem
                      key={`${key}:${t.droppableId}`}
                      type="button"
                      onClick={() => menuMove(media, t.sectionId)}
                      disabled={!canDropInto(t.droppableId, media)}
                    >
                      Move to {t.label}
                    </MenuItem>
                  ))}

                  {media?.can_delete && (
                    <MenuItem
                      type="button"
                      onClick={() => {
                        setOpenMenuKey(null);
                        if (onDelete) onDelete();
                      }}
                      disabled={!onDelete}
                      title={!onDelete ? "Delete not available here" : "Delete media"}
                    >
                      Delete
                    </MenuItem>
                  )}
                </Menu>,
                document.body
              )
            : null}
        </>
      );
    },
    [openMenuKey, menuPos, getMoveTargets, menuMove, canDropInto]
  );

  /* =============================================================================
   * Drag/drop handler
   * ========================================================================== */

  const onDragEnd = useCallback(
    async (result) => {
      const { source, destination } = result;
      if (!destination) return;
      if (!post?.id) return;

      const sourceId = source.droppableId;
      const destId = destination.droppableId;
      const sameList = sourceId === destId;

      const sourceList = [...getMediaListForDroppable(sourceId)];
      const moved = sourceList[source.index];

      if (!moved || !moved.id) return;
      if (!moved.can_move) return;

      if (sameList && sourceId === "inbox" && !isOwnerOrAdmin) {
        setDragNotice("Not allowed: only the post owner/admin can reorder the Inbox.");
        return;
      }

      if (!canDropInto(destId, moved)) {
        setDragNotice(
          "Not allowed: you can only move media you can edit, and only into moments you can edit."
        );
        return;
      }

      setDragNotice(null);

      sourceList.splice(source.index, 1);
      const destList = sameList ? sourceList : [...getMediaListForDroppable(destId)];
      destList.splice(destination.index, 0, moved);

      setMediaListForDroppable(sourceId, sourceList);
      setMediaListForDroppable(destId, destList);

      try {
        if (sameList) {
          const sectionIdOrNull =
            sourceId === "inbox" ? null : Number(sourceId.replace("section-", ""));
          await reorderMedia(sectionIdOrNull, destList.map((m) => m.id));
        } else {
          const targetSectionIdOrNull =
            destId === "inbox" ? null : Number(destId.replace("section-", ""));

          const after = destination.index === 0 ? null : destList[destination.index - 1]?.id || null;
          await moveMedia(moved.id, targetSectionIdOrNull, after);
        }
      } catch (e) {
        console.error("Drag/drop update failed:", e);
        setDragNotice("Update failed. Refreshing from server.");
        await refreshFromServer();
      }
    },
    [
      post?.id,
      getMediaListForDroppable,
      setMediaListForDroppable,
      canDropInto,
      isOwnerOrAdmin,
      reorderMedia,
      moveMedia,
      refreshFromServer,
    ]
  );

  /* =============================================================================
   * Sections editing helpers
   * ========================================================================== */

  const setSectionContent = useCallback((idx, html) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, content: html } : s)));
  }, []);

  const setSectionTitle = useCallback((idx, value) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, title: value } : s)));
  }, []);

  const normalizeSectionOrders = useCallback((list) => {
    return list.map((s, i) => ({ ...s, order: i + 1 }));
  }, []);

  const moveSection = useCallback(
    (fromIdx, toIdx) => {
      setSections((prev) => {
        if (fromIdx === toIdx || toIdx < 0 || toIdx >= prev.length) return prev;
        const next = [...prev];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        return normalizeSectionOrders(next);
      });
    },
    [normalizeSectionOrders]
  );

  const addSection = useCallback(() => {
    const tmpId = `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    // ensure editor ref slot exists (for focus)
    if (!editorRefs.current[tmpId]) editorRefs.current[tmpId] = { current: null };

    setSections((prev) => {
      const maxOrder = prev.reduce((m, s) => Math.max(m, Number(s.order) || 0), 0);
      return [
        ...prev,
        {
          id: null,
          _tmpId: tmpId,
          order: maxOrder + 1,
          title: "",
          content: "",
          author: currentAuthorName,
          author_username: user?.username || null,
          author_id: user?.id || null,
          can_edit: true,
          can_manage_media: true,
          media: [],
          pending_media: [],
        },
      ];
    });

    setLastAddedKey(tmpId);
  }, [currentAuthorName, user?.username, user?.id]);

  /* =============================================================================
   * Save (create/update)
   * ========================================================================== */

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        const [lat, lng] = coordinates;

        const finalLoc = location || (await fetchLocationName(lat, lng)) || "";
        const tzFromPin = await fetchTimeZone(lat, lng);
        const finalTZ =
          tzFromPin || timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "";

        const formData = new FormData();
        formData.append("title", title);

        const cleanedSections = (sections || [])
          .map((s) => {
            const payload = { id: s.id || null, order: Number(s.order) || 1 };
            if (!s.id || s.can_edit) {
              payload.title = s.title || "";
              payload.content = s.content || "";
            }
            return payload;
          })
          .sort((a, b) => a.order - b.order);

        formData.append("sections", JSON.stringify(cleanedSections));

        // ✅ Legacy sync: only if section #1 is editable (or new)
        const section1Local = (sections || []).find((s) => Number(s.order) === 1);
        if (section1Local && (!section1Local.id || section1Local.can_edit)) {
          formData.append("content", section1Local.content || "");
        }

        formData.append("location", finalLoc);
        formData.append("timezone", finalTZ);

        if (showDateControls) {
          formData.append("post_date", date);
        }

        formData.append("latitude", lat);
        formData.append("longitude", lng);

        formData.append("blog_id", selected_blog?.id);
        formData.append("images_to_delete", JSON.stringify(imagesToDelete));

        const saved = await onSave(formData, post?.id || null);

        // Post-save staged uploads
        if (saved?.id) {
          if (newImages.length > 0) {
            try {
              const created = await uploadToPostInbox(saved.id, newImages);
              const normalized = created.map((m) => ({ ...m, url: m.media_url || m.url }));
              setExistingImages((prev) => [...prev, ...normalized]);
              newImages.forEach((f) => f?.preview && URL.revokeObjectURL(f.preview));
              setNewImages([]);
            } catch (err) {
              console.error("Post inbox staged upload failed:", err);
              setDragNotice("Inbox staged upload failed.");
            }
          }

          const savedSections = Array.isArray(saved.sections) ? saved.sections : [];
          const byOrder = new Map(savedSections.map((ss) => [Number(ss.order), ss]));

          for (const local of sections) {
            const pending = local.pending_media || [];
            if (!pending.length) continue;

            const target = byOrder.get(Number(local.order));
            if (!target?.id) continue;

            try {
              const created = await uploadToSection(target.id, pending);
              setSections((prev) =>
                prev.map((s) =>
                  Number(s.order) !== Number(local.order)
                    ? s
                    : { ...s, media: [...(s.media || []), ...created], pending_media: [] }
                )
              );
              pending.forEach((f) => f?.preview && URL.revokeObjectURL(f.preview));
            } catch (err) {
              console.error("Section staged upload failed:", err);
              setDragNotice("Section staged upload failed.");
            }
          }
        }
      } catch (err) {
        console.error("Error saving post:", err);
        setDragNotice("Save failed.");
      } finally {
        setLoading(false);
      }
    },
    [
      setLoading,
      coordinates,
      location,
      timezone,
      title,
      sections,
      showDateControls,
      date,
      selected_blog?.id,
      imagesToDelete,
      onSave,
      post?.id,
      newImages,
      uploadToPostInbox,
      uploadToSection,
    ]
  );

  /* =============================================================================
   * Rendering helpers
   * ========================================================================== */

  const initialPosition = useMemo(
    () => ({ lat: Number(coordinates[0]), lng: Number(coordinates[1]) }),
    [coordinates]
  );

  /* =============================================================================
   * Guard rails
   * ========================================================================== */

  if (!selected_blog) return <p>Loading blog...</p>;
  if (!canEnterEditMode) {
    return <MutedMessage>You do not have permission to create or edit posts.</MutedMessage>;
  }

  /* =============================================================================
   * UI
   * ========================================================================== */

  return (
    <DragDropContext
      onDragStart={(start) => {
        setDragNotice(null);
        const raw = start?.draggableId || "";
        const id = raw.startsWith("media-") ? Number(raw.replace("media-", "")) : null;
        setActiveDragMediaId(Number.isFinite(id) ? id : null);
      }}
      onDragEnd={onDragEnd}
    >
      <Form onSubmit={handleSubmit}>
        <Input
          theme={theme}
          type="text"
          placeholder="Post Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        {!isEditing && canEditTimeline && (
          <ToggleRow>
            <input
              type="checkbox"
              checked={historicalMode}
              onChange={(e) => setHistoricalMode(e.target.checked)}
            />
            <span>Historical Mode</span>
            <span style={{ fontWeight: 400, opacity: 0.8 }}>(enable date/time on new posts)</span>
          </ToggleRow>
        )}

        {canEditTimeline && (isEditing || historicalMode) && (
          <>
            <Label htmlFor="post-date">Post Date &amp; Time (in post timezone)</Label>
            <Input
              theme={theme}
              id="post-date"
              type="datetime-local"
              value={date}
              onChange={(e) => {
                dateDirtyRef.current = true;
                setDate(e.target.value);
              }}
              required
            />

            <Label htmlFor="timezone">Timezone</Label>
            <Select
              theme={theme}
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              required
            >
              {timezones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </Select>
          </>
        )}

        <SectionHeader>
          <div className="meta">
            <span>Moments</span>
            <span className="sub">(each section saves via post save; permissions enforced server-side)</span>
          </div>

          <SmallBtn type="button" theme={theme} onClick={addSection} disabled={!canAddMoment}>
            + Add Moment
          </SmallBtn>
        </SectionHeader>

        {(sections || []).map((sec, idx) => {
          const secKey = sec.id || sec._tmpId || `fallback-${idx}`;
          if (!editorRefs.current[secKey]) editorRefs.current[secKey] = { current: null };

          const sectionEditable = sec.id ? !!sec.can_edit : true;
          const authorName = bestAuthorName(sec) || sec.author || currentAuthorName;
          const label = momentLabelFromAuthor(authorName);

          const canRemoveThisSection = (() => {
            if (Number(sec.order) === 1) return false;
            if (!sec.id) return !!canAddMoment;
            return isOwnerOrAdmin || (!!sec.can_edit && !!isEditing);
          })();

          return (
            <SectionCard
              key={secKey}
              theme={theme}
              ref={(el) => {
                if (el) sectionElRefs.current[secKey] = el;
                else delete sectionElRefs.current[secKey];
              }}
            >
              <SectionHeader>
                {Boolean(sec.id) && isOwnerOrAdmin && (
                  <SmallBtn
                    type="button"
                    onClick={async () => {
                      const sid = Number(sec.id);
                      const nextOpen = sectionGrantUIOpen === sid ? null : sid;
                      setSectionGrantUIOpen(nextOpen);

                      if (nextOpen) {
                        await loadCollaborators();
                        await loadSectionGrants(sid);
                      }
                    }}
                  >
                    Collaborators
                  </SmallBtn>
                )}

                <div className="meta">
                  <span>{label}</span>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  {canReorderMoments && (
                    <>
                      <SmallBtn
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveSection(idx, idx - 1)}
                        title="Move up"
                      >
                        Up
                      </SmallBtn>
                      <SmallBtn
                        type="button"
                        disabled={idx === (sections?.length || 0) - 1}
                        onClick={() => moveSection(idx, idx + 1)}
                        title="Move down"
                      >
                        Down
                      </SmallBtn>
                    </>
                  )}
                  {canRemoveThisSection && (
                    <DangerBtn type="button" onClick={() => removeSection(idx)}>
                      Remove
                    </DangerBtn>
                  )}
                </div>
              </SectionHeader>

              {sectionEditable ? (
                <>
                  <Label>Moment title (optional)</Label>
                  <Input
                    type="text"
                    value={sec.title || ""}
                    placeholder="Add a short title for this moment"
                    onChange={(e) => setSectionTitle(idx, e.target.value)}
                  />
                </>
              ) : (
                sec.title && (
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    {sec.title}
                  </div>
                )
              )}

              {Boolean(sec.id) && sectionGrantUIOpen === Number(sec.id) && (
                <div
                  style={{
                    marginTop: 10,
                    padding: 10,
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 10,
                  }}
                >
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>
                    Who can edit this moment?
                  </div>

                  {collabMembers.length === 0 ? (
                    <MutedMessage>
                      Only the post owner/admin can manage collaborators (or none found).
                    </MutedMessage>
                  ) : (
                    collabMembers.slice(0, MAX_GRANT_ROWS).map((m) => {
                      const sid = Number(sec.id);
                      const perms =
                        sectionGrantDrafts?.[sid]?.[m.username] || {
                          can_edit_content: false,
                          can_manage_media: false,
                        };

                      return (
                        <div
                          key={`${sid}:${m.username}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginBottom: 6,
                          }}
                        >
                          <div style={{ minWidth: 180 }}>
                            <strong>{m.display_name || m.username}</strong>
                            <span style={{ opacity: 0.65 }}> ({m.role})</span>
                          </div>

                          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                              type="checkbox"
                              checked={!!perms.can_edit_content}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSectionGrantDrafts((prev) => {
                                  const next = { ...(prev || {}) };
                                  next[sid] = { ...(next[sid] || {}) };
                                  next[sid][m.username] = {
                                    ...(next[sid][m.username] || {}),
                                    can_edit_content: checked,
                                  };
                                  return next;
                                });
                              }}
                            />
                            Edit content
                          </label>

                          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                              type="checkbox"
                              checked={!!perms.can_manage_media}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSectionGrantDrafts((prev) => {
                                  const next = { ...(prev || {}) };
                                  next[sid] = { ...(next[sid] || {}) };
                                  next[sid][m.username] = {
                                    ...(next[sid][m.username] || {}),
                                    can_manage_media: checked,
                                  };
                                  return next;
                                });
                              }}
                            />
                            Manage media
                          </label>
                        </div>
                      );
                    })
                  )}

                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <SmallBtn type="button" onClick={() => saveSectionGrants(Number(sec.id))}>
                      Save collaborators
                    </SmallBtn>
                    <SmallBtn type="button" onClick={() => setSectionGrantUIOpen(null)}>
                      Close
                    </SmallBtn>
                  </div>
                </div>
              )}

              {!sectionEditable && (
                <MutedMessage>
                  You can view this moment, but you don’t have permission to edit it.
                </MutedMessage>
              )}

              {sectionEditable ? (
                <TinyMceEditor
                  value={sec.content ?? ""}
                  onChange={(html) => setSectionContent(idx, html)}
                  disabled={false}
                  editorRef={editorRefs.current[secKey]}
                />
              ) : (
                <ReadOnlyHtml dangerouslySetInnerHTML={{ __html: sec.content || "" }} />
              )}

              {(sectionEditable || sec.can_manage_media) && (
                <SectionMediaDropzone
                  theme={theme}
                  onFiles={(fileArr) => addMediaToSection(idx, fileArr)}
                />
              )}

              <MediaPreview>
                {sec.id ? (
                  <Droppable droppableId={`section-${sec.id}`} direction="horizontal">
                    {(provided, snapshot) => {
                      const dragged = getDraggedMedia();
                      const allowed = dragged ? canDropInto(`section-${sec.id}`, dragged) : true;
                      const shellState = snapshot.isDraggingOver
                        ? allowed
                          ? "allowed"
                          : "blocked"
                        : "idle";

                      return (
                        <>
                          <MediaStripLabel>Moment Media (drop from Inbox here)</MediaStripLabel>

                          <DroppableShell
                            $state={shellState}
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {(sec.media || []).length === 0 && (
                              <EmptyDroppableHint>
                                Drop an item from the Post Inbox here to attach it to this moment.
                              </EmptyDroppableHint>
                            )}

                            <DndMediaList>
                              {(sec.media || []).map((m, index) => (
                                <Draggable
                                  key={m.id}
                                  draggableId={`media-${m.id}`}
                                  index={index}
                                  isDragDisabled={!canDropInto(`section-${sec.id}`, m)}
                                >
                                  {(dragProvided) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      style={{ ...dragProvided.draggableProps.style }}
                                    >
                                      <Preview theme={theme}>
                                        {m.can_move && (
                                          <DragHandle
                                            {...dragProvided.dragHandleProps}
                                            title="Drag to move/reorder"
                                          >
                                            ≡
                                          </DragHandle>
                                        )}

                                        {renderMediaMenu(
                                          m,
                                          `section:${sec.id}:${m.id}`,
                                          () => removeSectionMedia(idx, m.id)
                                        )}

                                        {m.content_type?.startsWith("video") ? (
                                          <img
                                            src={m.poster_url || "/media/defaults/video-placeholder.png"}
                                            alt="video thumbnail"
                                            onClick={() => window.open(m.media_url)}
                                            title={m.processed ? "Click to view video" : "Processing..."}
                                            style={{ opacity: m.processed ? 1 : 0.5 }}
                                          />
                                        ) : (
                                          <picture>
                                            {m.webp_url && <source srcSet={m.webp_url} type="image/webp" />}
                                            <img
                                              src={m.thumbnail_url || m.media_url}
                                              alt="section media"
                                              onClick={() => window.open(m.media_url)}
                                            />
                                          </picture>
                                        )}

                                        {m.can_delete && (
                                          <RemoveButton
                                            type="button"
                                            onClick={() => removeSectionMedia(idx, m.id)}
                                          >
                                            &times;
                                          </RemoveButton>
                                        )}
                                      </Preview>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </DndMediaList>
                          </DroppableShell>
                        </>
                      );
                    }}
                  </Droppable>
                ) : null}

                {(sec.pending_media || []).map((file, fidx) => (
                  <Preview key={`pending-${secKey}-${fidx}`} theme={theme}>
                    {file.type?.startsWith("image") ? (
                      <img src={file.preview} alt="preview" />
                    ) : (
                      <video src={file.preview} muted controls />
                    )}
                    <RemoveButton
                      type="button"
                      onClick={() => {
                        setSections((prev) =>
                          prev.map((s, i) => {
                            if (i !== idx) return s;
                            const next = [...(s.pending_media || [])];
                            const target = next[fidx];
                            if (target?.preview) URL.revokeObjectURL(target.preview);
                            next.splice(fidx, 1);
                            return { ...s, pending_media: next };
                          })
                        );
                      }}
                    >
                      &times;
                    </RemoveButton>
                  </Preview>
                ))}
              </MediaPreview>
            </SectionCard>
          );
        })}

        {post?.id && (
          <MetricsPanel theme={theme}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Metrics (Last 30 Days)</div>
            {metricsLoading && <MutedMessage>Loading metrics...</MutedMessage>}
            {!metricsLoading && postMetrics && (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  {typeof postMetrics.views_total === "number" && (
                    <div><strong>Post Views:</strong> {postMetrics.views_total}</div>
                  )}
                  {typeof postMetrics.reactions_total === "number" && (
                    <div><strong>Reactions:</strong> {postMetrics.reactions_total}</div>
                  )}
                  {typeof postMetrics.comments_total === "number" && (
                    <div><strong>Comments:</strong> {postMetrics.comments_total}</div>
                  )}
                  {typeof postMetrics.section_views_total === "number" && (
                    <div><strong>Section Views:</strong> {postMetrics.section_views_total}</div>
                  )}
                  {typeof postMetrics.media_views_total === "number" && (
                    <div><strong>Media Views:</strong> {postMetrics.media_views_total}</div>
                  )}
                </div>

                {Array.isArray(postMetrics.sections) && postMetrics.sections.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Per‑Moment</div>
                    {postMetrics.sections.map((s) => (
                      <div key={s.section_id} style={{ marginBottom: 4 }}>
                        <strong>
                          {s.title || `Moment ${s.order}`}
                        </strong>
                        : {s.views_total ?? 0} views, {s.media_views_total ?? 0} media views
                      </div>
                    ))}
                  </div>
                )}

                {postMetricsTs && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Engagement Timeline</div>
                    <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>
                      {["views", "reactions", "comments"].map((k) => (
                        <div key={k}>
                          <strong>{k}:</strong>{" "}
                          {(postMetricsTs?.[k] || []).map((row) => row.count).join(", ") || "—"}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </MetricsPanel>
        )}

        <Suspense fallback={null}>
          <MapWrapper>
            <MapPicker
              initialPosition={initialPosition}
              allowGeolocation={!isEditing}
              locationName={location}
              timeZoneId={timezone}
              onLocationChange={({ lat, lng }) => {
                const nLat = Number(lat);
                const nLng = Number(lng);
                if (Number.isNaN(nLat) || Number.isNaN(nLng)) return;

                const [cLat, cLng] = coordinates;
                const same = approxEqual(cLat, nLat) && approxEqual(cLng, nLng);
                if (!same) setCoordinates([nLat, nLng]);
              }}
            />
          </MapWrapper>
        </Suspense>

        <div {...getRootProps()}>
          <Dropzone theme={theme} style={{ opacity: canUploadInbox ? 1 : 0.6 }}>
            <input {...getInputProps()} />
            <p>Drag and drop images or videos here, or click to select files</p>
          </Dropzone>
        </div>

        {!canUploadInbox && (
          <MutedMessage>
            Only the post owner (or an admin) can upload media to the post Inbox. Upload to a moment instead.
          </MutedMessage>
        )}

        <MediaPreview>
          {post?.id ? (
            <Droppable droppableId="inbox" direction="horizontal">
              {(provided, snapshot) => {
                const dragged = getDraggedMedia();
                const allowed = dragged ? canDropInto("inbox", dragged) : true;
                const shellState = snapshot.isDraggingOver
                  ? allowed
                    ? "allowed"
                    : "blocked"
                  : "idle";

                return (
                  <DroppableShell
                    $state={shellState}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <DndMediaList>
                      {existingImages.map((img, index) => {
                        const isPending = !!img.is_pending;
                        const previewSrc = img.preview_url || img.thumbnail_url || img.url;
                        return (
                        <Draggable
                          key={img.id}
                          draggableId={`media-${img.id}`}
                          index={index}
                          isDragDisabled={
                            !canDropInto("inbox", img) ||
                            (!isOwnerOrAdmin && img.uploaded_by !== user?.username)
                          }
                        >
                          {(dragProvided) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={{ ...dragProvided.draggableProps.style }}
                            >
                              <Preview theme={theme}>
                                {img.can_move && (
                                  <DragHandle {...dragProvided.dragHandleProps} title="Drag to move/reorder">
                                    ≡
                                  </DragHandle>
                                )}

                                {renderMediaMenu(img, `inbox:${img.id}`, () => removeExistingImage(img.id))}

                                {img.content_type?.startsWith("video") ? (
                                  isPending && img.preview_url ? (
                                    <video src={img.preview_url} muted controls />
                                  ) : (
                                    <img
                                      src={img.poster_url || "/media/defaults/video-placeholder.png"}
                                      alt="video thumbnail"
                                      onClick={() => window.open(img.url)}
                                      title={img.processed ? "Click to view video" : "Processing..."}
                                      style={{ opacity: img.processed ? 1 : 0.5 }}
                                    />
                                  )
                                ) : (
                                  <picture>
                                    {img.webp_url && <source srcSet={img.webp_url} type="image/webp" />}
                                    <img
                                      src={previewSrc}
                                      alt={img.filename || "existing"}
                                      onClick={() => window.open(img.url)}
                                    />
                                  </picture>
                                )}

                                {img.can_delete && (
                                  <RemoveButton type="button" onClick={() => removeExistingImage(img.id)}>
                                    &times;
                                  </RemoveButton>
                                )}
                              </Preview>
                            </div>
                          )}
                        </Draggable>
                      )})}
                      {provided.placeholder}
                    </DndMediaList>
                  </DroppableShell>
                );
              }}
            </Droppable>
          ) : null}

          {newImages.map((file, idx) => (
            <Preview key={`new-${idx}`} theme={theme}>
              {file.type?.startsWith("image") ? (
                <img src={file.preview} alt="preview" onClick={() => window.open(file.preview)} />
              ) : (
                <video src={file.preview} muted controls onClick={() => window.open(file.preview)} />
              )}
              <RemoveButton type="button" onClick={() => removeNewImage(idx)}>
                &times;
              </RemoveButton>
            </Preview>
          ))}
        </MediaPreview>

        {dragNotice && <InlineNotice>{dragNotice}</InlineNotice>}

        <ButtonRow theme={theme}>
          <button type="submit">{isEditing ? "Update Post" : "Create Post"}</button>
          {onCancel && (
            <button type="button" onClick={onCancel}>
              Cancel
            </button>
          )}
        </ButtonRow>
      </Form>
    </DragDropContext>
  );
};

export default PostForm;
